/* import __COLOCATED_TEMPLATE__ from './fin-phone-issue-categorization.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';
import type RouterService from '@ember/routing/router-service';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    accordion: any;
    numberConfiguration?: FinVoiceNumberConfiguration;
    updateFinIssueCategorization: (attributes?: string[]) => Promise<void>;
  };
}

export default class FinPhoneIssueCategorization extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare finVoiceConfigService: FinVoiceConfigService;
  @service declare notificationsService: $TSFixMe;
  @service declare router: RouterService;
  @service declare intercomConfirmService: IntercomConfirmService;

  @tracked selectedAttributes: string[] = [];

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);

    this.resetFinIssueCategorization();
  }

  get isSaving() {
    return Boolean(this.numberConfiguration?.isSaving);
  }

  get hasAttributeChanges() {
    if (this.selectedAttributes.length !== this.savedAttributes?.length) {
      return true;
    }

    let selectedSet = new Set(this.selectedAttributes);
    let savedSet = new Set(this.savedAttributes);

    return (
      selectedSet.size !== savedSet.size || !this.selectedAttributes.every((id) => savedSet.has(id))
    );
  }

  get savingDisabled() {
    return Boolean(this.isSaving || !this.hasAttributeChanges);
  }

  get errorMessage() {
    if (!this.hasAttributeChanges) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.fin-issue-categorization.error-no-changes',
      );
    }

    return undefined;
  }

  get numberConfiguration() {
    return this.args.numberConfiguration;
  }

  get savedAttributes() {
    return this.numberConfiguration?.classificationAttributes;
  }

  get activeCategoriesCount() {
    return this.intl.t(
      'operator.fin.setup.tabs.setup-and-go-live.phone.fin-issue-categorization.subtitle',
      {
        count: this.savedAttributes?.length || 0,
      },
    );
  }

  get allClassifiableListAttributes() {
    return this.finVoiceConfigService.conversationAttributeDescriptors;
  }

  @action
  resetFinIssueCategorization() {
    this.selectedAttributes = this.savedAttributes || [];
  }

  @action
  addSelectedAttributes(_: string, updatedList: string[]) {
    this.selectedAttributes = updatedList;
  }

  @action
  removeSelectedAttribute(_: string, updatedList: string[]) {
    this.selectedAttributes = updatedList;
  }

  @action
  async updateFinIssueCategorization() {
    if (!this.hasAttributeChanges) {
      return;
    }

    await this.args.updateFinIssueCategorization(this.selectedAttributes);
  }

  async checkUnsavedChangesBeforeNavigating() {
    if (!this.hasAttributeChanges) {
      return false;
    }

    let confirmed = await this.intercomConfirmService.confirm({
      title: this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.fin-issue-categorization.unsaved-changes-modal.title',
      ),
      body: this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.fin-issue-categorization.unsaved-changes-modal.body',
      ),
      confirmButtonText: this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.fin-issue-categorization.unsaved-changes-modal.confirm',
      ),
      cancelButtonText: this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.fin-issue-categorization.unsaved-changes-modal.cancel',
      ),
    });

    return !confirmed;
  }

  @action
  async navigateToRoute(route: string) {
    if (await this.checkUnsavedChangesBeforeNavigating()) {
      return;
    }

    this.router.transitionTo(route);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::FinVoice::Editor::FinPhoneIssueCategorization': typeof FinPhoneIssueCategorization;
  }
}
